import { AxiosError } from 'axios';
import { TranslateFunction } from '~/Locale';
import { formatMoney } from './currency';
import { log } from './bugsnagHelper';

export enum PlaceBidError {
  BID_TOO_LOW = 'BID_TOO_LOW',
  BID_TOO_HIGH = 'BID_TOO_HIGH',
  MAX_BID_DUPLICATION = 'MAX_BID_DUPLICATION',
  MEMBER_MAX_AUCTIONS_REACHED = 'MEMBER_MAX_AUCTIONS_REACHED'
}

export const handleErrorPlacingBid = (
  error: Error | AxiosError<{ message: PlaceBidError }>,
  {
    t,
    maxAllowedBid,
    bidAmount,
    minBidRaise
  }: {
    maxAllowedBid: number;
    t: TranslateFunction;
    bidAmount?: number;
    minBidRaise?: number;
  }
) => {
  // if we get a 5XX error, the response key in error is undefined
  if ('response' in error) {
    switch (error.response?.data.message) {
      case PlaceBidError.BID_TOO_LOW:
        log(new Error(`Error when placing bid - Bid too low`), {
          severity: 'info',
          error
        });

        return t('Bid raise was too low');
        break;
      case PlaceBidError.MAX_BID_DUPLICATION:
        log(new Error(`Error when placing bid - Bid already exists`), {
          severity: 'info',
          error
        });

        if (bidAmount !== undefined && minBidRaise !== undefined) {
          const lowestPossibleBid = bidAmount + minBidRaise;
          return t(
            `The bid you have entered is already taken by another bidder's automatic bid. In order for your bid to be registered, you need to increase it by an additional %1$s to %2$s.`,
            formatMoney({ value: minBidRaise, currency: t('SEK') }),
            formatMoney({ value: lowestPossibleBid, currency: t('SEK') })
          );
        }

        return t(
          'The specified maximum bid already exists. Please enter another amount.'
        );
        break;
      case PlaceBidError.BID_TOO_HIGH:
        log(new Error(`Error when placing bid - Bid too high`), {
          severity: 'info',
          error
        });

        return maxAllowedBid
          ? t(
              `Your bid is too high, maximum bid is %1$s`,
              formatMoney({
                value: maxAllowedBid,
                currency: t('SEK')
              })
            )
          : t('Your bid is too high');

        break;
      case 'MEMBER_MAX_AUCTIONS_REACHED':
        log(new Error(`Error when placing bid - Bidded on too many auctions`), {
          severity: 'info',
          error
        });

        return t('You are not allowed to bid on more auctions');
        break;
      default:
        log(new Error(`Error when placing bid`), {
          severity: 'warning',
          error
        });

        return t('Something went wrong, the bid could not be placed');
        break;
    }
  }

  // Don't show the generic error message for 401, 401 will either retry or log the user out.
  if (error?.message === 'Network Error') {
    return t(
      'The bid could not be placed. Please check your network connection.'
    );
  }

  log(new Error('Unexpected error when placing bid'), {
    error
  });

  return t('Something went wrong, the bid could not be placed');
};
